import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Card, Input, message, Modal, Popconfirm, Progress, Select, Space, Table, Tag, Typography, Upload } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFirebase } from 'containers/FirebaseContext';
import dayjs from 'utils/dayjsConfig';
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  doc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  setDoc,
  arrayUnion,
  arrayRemove,
  writeBatch,
} from 'firebase/firestore';
import { uploadToFirebaseStorage } from 'utils/methods/firebaseUploadImages';

const { Title } = Typography;

const AUDIO_COLLECTION = import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'audio_gallery_prod' : 'audio_gallery';
const META_COLLECTION = import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'audio_gallery_meta_prod' : 'audio_gallery_meta';
const GENRE_CATALOG_DOC_ID = 'genre_catalog';

const isAudioFile = (file) => {
  const type = file?.type || '';
  return Boolean(file) && (type.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(file.name));
};

const formatDate = (ts) => {
  try {
    return ts?.toDate ? dayjs(ts.toDate()).format('YYYY-MM-DD HH:mm') : '';
  } catch (e) {
    return '';
  }
};

const normalizeGenres = (item) => {
  if (Array.isArray(item?.genres) && item.genres.length) {
    return item.genres.map((g) => String(g || '').trim()).filter(Boolean);
  }
  const single = String(item?.genre || '').trim();
  return single ? [single] : [];
};

const genresLabel = (item) => {
  const g = normalizeGenres(item);
  return g.length ? g.join(', ') : '';
};

const sortGenreList = (list) => [...(list || [])].map((x) => String(x || '').trim()).filter(Boolean).sort((a, b) => a.localeCompare(b));

const fileSignature = (f) => `${f?.name || ''}\0${f?.size || 0}\0${f?.lastModified || 0}`;

export default function AudioGalleryAdmin() {
  const { firestore } = useFirebase();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [catalogGenres, setCatalogGenres] = useState([]);

  const [addGenreOpen, setAddGenreOpen] = useState(false);
  const [newGenreName, setNewGenreName] = useState('');
  const [savingGenre, setSavingGenre] = useState(false);
  const [genreEditOpen, setGenreEditOpen] = useState(false);
  const [genreEditOriginal, setGenreEditOriginal] = useState('');
  const [genreEditNext, setGenreEditNext] = useState('');
  const [savingGenreEdit, setSavingGenreEdit] = useState(false);
  const [deletingGenre, setDeletingGenre] = useState(false);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadGenre, setUploadGenre] = useState(undefined);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileListKey, setUploadFileListKey] = useState(0);

  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editGenre, setEditGenre] = useState(undefined);
  const [editFile, setEditFile] = useState(null);
  const [editFileName, setEditFileName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const editFileInputRef = useRef(null);
  const [editFileInputKey, setEditFileInputKey] = useState(0);

  useEffect(() => {
    if (!firestore) return;

    setFetching(true);
    const q = query(collection(firestore, AUDIO_COLLECTION), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(next);
        setFetching(false);
      },
      () => {
        setItems([]);
        setFetching(false);
      }
    );

    return () => unsub();
  }, [firestore]);

  useEffect(() => {
    if (!firestore) return;
    const ref = doc(firestore, META_COLLECTION, GENRE_CATALOG_DOC_ID);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const raw = snap.data()?.list;
        setCatalogGenres(sortGenreList(Array.isArray(raw) ? raw : []));
      },
      () => setCatalogGenres([])
    );
    return () => unsub();
  }, [firestore]);

  const genreOptions = useMemo(
    () => catalogGenres.map((g) => ({ label: g, value: g })),
    [catalogGenres]
  );

  const editGenreOptions = useMemo(() => {
    const opts = catalogGenres.map((g) => ({ label: g, value: g }));
    if (editGenre && !catalogGenres.includes(editGenre)) {
      return [{ label: editGenre, value: editGenre }, ...opts];
    }
    return opts;
  }, [catalogGenres, editGenre]);

  const closeAddGenreModal = () => {
    setAddGenreOpen(false);
    setNewGenreName('');
    setSavingGenre(false);
    setGenreEditOpen(false);
    setGenreEditOriginal('');
    setGenreEditNext('');
    setSavingGenreEdit(false);
    setDeletingGenre(false);
  };

  const onSaveNewGenre = async () => {
    const trimmed = (newGenreName || '').trim();
    if (!trimmed) {
      message.warning('Enter a genre name');
      return;
    }
    if (catalogGenres.some((g) => g.toLowerCase() === trimmed.toLowerCase())) {
      message.warning('This genre already exists');
      return;
    }
    if (!firestore) return;
    setSavingGenre(true);
    try {
      await setDoc(doc(firestore, META_COLLECTION, GENRE_CATALOG_DOC_ID), { list: arrayUnion(trimmed) }, { merge: true });
      message.success('Genre added');
      closeAddGenreModal();
    } catch (e) {
      message.error('Failed to save genre');
      setSavingGenre(false);
    }
  };

  const isGenreLinkedToAnyAudio = (genreName) => {
    const g = String(genreName || '').trim();
    if (!g) return false;
    const gl = g.toLowerCase();
    return (items || []).some((it) => normalizeGenres(it).some((x) => String(x || '').trim().toLowerCase() === gl));
  };

  const openGenreEdit = (genreName) => {
    const g = String(genreName || '').trim();
    setGenreEditOriginal(g);
    setGenreEditNext(g);
    setGenreEditOpen(true);
  };

  const closeGenreEdit = () => {
    if (savingGenreEdit) return;
    setGenreEditOpen(false);
    setGenreEditOriginal('');
    setGenreEditNext('');
    setSavingGenreEdit(false);
  };

  const onSaveGenreEdit = async () => {
    const original = String(genreEditOriginal || '').trim();
    const next = String(genreEditNext || '').trim();
    if (!original) return;
    if (!next) {
      message.warning('Enter a genre name');
      return;
    }
    if (original.toLowerCase() === next.toLowerCase()) {
      closeGenreEdit();
      return;
    }
    if (catalogGenres.some((g) => String(g || '').trim().toLowerCase() === next.toLowerCase())) {
      message.warning('This genre already exists');
      return;
    }
    if (!firestore) return;

    setSavingGenreEdit(true);
    try {
      const ref = doc(firestore, META_COLLECTION, GENRE_CATALOG_DOC_ID);
      await setDoc(ref, { list: arrayRemove(original) }, { merge: true });
      await setDoc(ref, { list: arrayUnion(next) }, { merge: true });

      const affected = (items || []).filter((it) =>
        normalizeGenres(it).some((x) => String(x || '').trim().toLowerCase() === original.toLowerCase())
      );

      if (affected.length) {
        const chunkSize = 450;
        for (let i = 0; i < affected.length; i += chunkSize) {
          const batch = writeBatch(firestore);
          const chunk = affected.slice(i, i + chunkSize);
          chunk.forEach((it) => {
            const replaced = normalizeGenres(it).map((x) =>
              String(x || '').trim().toLowerCase() === original.toLowerCase() ? next : x
            );
            const finalGenres = replaced.length ? replaced : [next];
            batch.update(doc(firestore, AUDIO_COLLECTION, it.id), {
              genres: finalGenres,
              genre: finalGenres[0],
              updatedAt: serverTimestamp(),
            });
          });
          await batch.commit();
        }
      }

      message.success('Genre updated');
      closeGenreEdit();
    } catch (e) {
      message.error('Failed to update genre');
      setSavingGenreEdit(false);
    }
  };

  const onDeleteGenre = async (genreName) => {
    const g = String(genreName || '').trim();
    if (!g) return;
    if (isGenreLinkedToAnyAudio(g)) {
      message.warning('This genre is already attached to audio. Please update those before deleting.');
      return;
    }
    if (!firestore) return;
    setDeletingGenre(true);
    try {
      await setDoc(doc(firestore, META_COLLECTION, GENRE_CATALOG_DOC_ID), { list: arrayRemove(g) }, { merge: true });
      message.success('Genre deleted');
    } catch (e) {
      message.error('Failed to delete genre');
    } finally {
      setDeletingGenre(false);
    }
  };

  const audioUploadFileList = useMemo(
    () =>
      uploadQueue.map(({ file: f }, i) => ({
        uid: `${f.name}-${f.size}-${f.lastModified}-${i}`,
        name: f.name,
        status: 'done',
        originFileObj: f,
      })),
    [uploadQueue]
  );

  const setQueueItemDisplayName = (index, value) => {
    setUploadQueue((q) => q.map((row, i) => (i === index ? { ...row, displayName: value } : row)));
  };

  const openUploadModal = () => {
    setUploadGenre(undefined);
    setUploadQueue([]);
    setUploadProgress(0);
    setUploadFileListKey((k) => k + 1);
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    if (uploading) return;
    setUploadModalOpen(false);
    setUploadGenre(undefined);
    setUploadQueue([]);
    setUploadFileListKey((k) => k + 1);
  };

  const onUpload = async () => {
    const picked = String(uploadGenre || '').trim();
    if (!picked) {
      message.warning('Select a genre');
      return;
    }
    const finalGenres = [picked];
    if (!uploadQueue.length) {
      message.warning('Please select one or more audio files');
      return;
    }
    const validRows = uploadQueue.filter((row) => isAudioFile(row.file));
    if (!validRows.length) {
      message.warning('Please select valid audio files');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const total = validRows.length;
      let done = 0;
      for (let i = 0; i < validRows.length; i += 1) {
        const { file: f, displayName: rowDisplay } = validRows[i];
        const url = await uploadToFirebaseStorage(f, (p) => setUploadProgress(Math.round((done + p / 100) / total * 100)), 'admin/audio');
        const fileName = (rowDisplay || '').trim() || f.name;
        await addDoc(collection(firestore, AUDIO_COLLECTION), {
          genres: finalGenres,
          genre: finalGenres[0],
          url,
          fileName,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        done += 1;
        setUploadProgress(Math.round((done / total) * 100));
      }
      setUploadGenre(undefined);
      setUploadQueue([]);
      setUploadFileListKey((k) => k + 1);
      message.success(validRows.length > 1 ? `${validRows.length} audios uploaded` : 'Audio uploaded successfully');
      setUploadModalOpen(false);
    } catch (e) {
      message.error('Failed to upload audio');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const openEdit = (item) => {
    setEditId(item.id);
    const existing = normalizeGenres(item);
    setEditGenre(existing[0] || undefined);
    setEditFile(null);
    setEditFileName(item.fileName || '');
    setEditUrl(item.url || '');
    if (editFileInputRef.current) {
      editFileInputRef.current.value = '';
    }
    setEditFileInputKey((k) => k + 1);
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditId(null);
    setEditGenre(undefined);
    setEditFile(null);
    setEditFileName('');
    setEditUrl('');
    setSaving(false);
    if (editFileInputRef.current) {
      editFileInputRef.current.value = '';
    }
    setEditFileInputKey((k) => k + 1);
  };

  const onSaveEdit = async () => {
    if (!editId) return;
    const picked = String(editGenre || '').trim();
    if (!picked) {
      message.warning('Select a genre');
      return;
    }
    const finalGenres = [picked];

    setSaving(true);
    try {
      let nextUrl = editUrl;
      let nextFileName = items.find((x) => x.id === editId)?.fileName || '';
      const finalEditFileName = (editFileName || '').trim();
      if (finalEditFileName) {
        nextFileName = finalEditFileName;
      }
      if (editFile) {
        if (!isAudioFile(editFile)) {
          message.warning('Please select a valid audio file');
          setSaving(false);
          return;
        }
        nextUrl = await uploadToFirebaseStorage(editFile, undefined, 'admin/audio');
        if (!finalEditFileName) {
          nextFileName = editFile.name;
        }
      }

      await updateDoc(doc(firestore, AUDIO_COLLECTION, editId), {
        genres: finalGenres,
        genre: finalGenres[0],
        url: nextUrl || '',
        fileName: nextFileName,
        updatedAt: serverTimestamp(),
      });

      message.success('Audio updated successfully');
      closeEdit();
    } catch (e) {
      message.error('Failed to update audio');
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteDoc(doc(firestore, AUDIO_COLLECTION, id));
      message.success('Audio deleted');
    } catch (e) {
      message.error('Failed to delete audio');
    }
  };

  const filteredItems = useMemo(() => {
    const q = (searchText || '').trim().toLowerCase();
    if (!q) return items || [];
    return (items || []).filter((it) => {
      const gStr = genresLabel(it).toLowerCase();
      const f = (it?.fileName || '').toLowerCase();
      return gStr.includes(q) || f.includes(q);
    });
  }, [items, searchText]);

  const pagedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return (filteredItems || []).slice(start, start + pageSize);
  }, [filteredItems, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, pageSize]);

  const tableColumns = useMemo(
    () => [
      {
        title: 'Genres',
        key: 'genres',
        width: 220,
        render: (_, row) => {
          const g = normalizeGenres(row);
          if (!g.length) return <Tag>N/A</Tag>;
          return (
            <Space size={[4, 4]} wrap>
              {g.map((name, idx) => (
                <Tag key={`${row.id}-${idx}`} color="blue">
                  {name}
                </Tag>
              ))}
            </Space>
          );
        },
      },
      {
        title: 'File',
        dataIndex: 'fileName',
        key: 'fileName',
        render: (v) => <span style={{ wordBreak: 'break-word' }}>{v || ''}</span>,
      },
      {
        title: 'Audio',
        key: 'audio',
        width: 260,
        render: (_, row) =>
          row?.url ? <audio controls src={row.url} style={{ width: 240 }} /> : <span style={{ opacity: 0.7 }}>No audio</span>,
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 180,
        render: (v) => <span style={{ opacity: 0.7 }}>{formatDate(v) || ''}</span>,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        render: (_, row) => (
          <Space>
            <Button icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
            <Popconfirm title="Delete this audio?" onConfirm={() => onDelete(row.id)} okText="Yes" cancelText="No">
              <Button danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [items]
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Audio
          </Title>
          <Space wrap>
            <Button icon={<PlusOutlined />} onClick={() => setAddGenreOpen(true)} disabled={!firestore}>
              Add genre
            </Button>
            <Button type="primary" icon={<UploadOutlined />} onClick={openUploadModal} disabled={!firestore}>
              Add audio
            </Button>
            <Input
              style={{ width: 280 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by genre or file name"
            />
          </Space>
        </div>

        {fetching ? (
          <div style={{ opacity: 0.7 }}>Loading...</div>
        ) : (
          <Table
            rowKey={(r) => r.id}
            columns={tableColumns}
            dataSource={pagedItems}
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredItems?.length || 0,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
          />
        )}
      </Card>

      <Modal
        title="Add genre"
        open={addGenreOpen}
        onCancel={closeAddGenreModal}
        width={480}
        footer={[
          <Button key="cancel" onClick={closeAddGenreModal} disabled={savingGenre}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={savingGenre} onClick={onSaveNewGenre}>
            Save
          </Button>,
        ]}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input
            value={newGenreName}
            onChange={(e) => setNewGenreName(e.target.value)}
            placeholder="Genre name"
            disabled={savingGenre}
            onPressEnter={() => !savingGenre && onSaveNewGenre()}
          />
          <div>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>All genres</div>
            <div
              style={{
                maxHeight: 280,
                overflow: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 12,
                background: '#fafafa',
              }}
            >
              {catalogGenres.length ? (
                <Space size={[8, 8]} wrap>
                  {catalogGenres.map((g) => (
                    <Tag
                      key={g}
                      color="blue"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, paddingRight: 6 }}
                      closable={false}
                    >
                      <span>{g}</span>
                      <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => openGenreEdit(g)}
                        disabled={savingGenre || savingGenreEdit || deletingGenre}
                      />
                      <Popconfirm
                        title="Delete this genre?"
                        okText="Yes"
                        cancelText="No"
                        onConfirm={() => onDeleteGenre(g)}
                      >
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={savingGenre || savingGenreEdit || deletingGenre}
                        />
                      </Popconfirm>
                    </Tag>
                  ))}
                </Space>
              ) : (
                <span style={{ opacity: 0.65 }}>No genres yet. Add one above.</span>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title="Edit genre"
        open={genreEditOpen}
        onCancel={closeGenreEdit}
        width={480}
        footer={[
          <Button key="cancel" onClick={closeGenreEdit} disabled={savingGenreEdit}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={savingGenreEdit} onClick={onSaveGenreEdit}>
            Save
          </Button>,
        ]}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ opacity: 0.75, fontSize: 13 }}>
            Renaming updates the genre catalog and any audio already using <b>{genreEditOriginal}</b>.
          </div>
          <Input
            value={genreEditNext}
            onChange={(e) => setGenreEditNext(e.target.value)}
            placeholder="Genre name"
            disabled={savingGenreEdit}
            onPressEnter={() => !savingGenreEdit && onSaveGenreEdit()}
          />
        </div>
      </Modal>

      <Modal
        title="Add audio"
        open={uploadModalOpen}
        onCancel={closeUploadModal}
        width={680}
        footer={[
          <Button key="cancel" onClick={closeUploadModal} disabled={uploading}>
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            icon={<UploadOutlined />}
            onClick={onUpload}
            loading={uploading}
            disabled={!firestore || uploading || !String(uploadGenre || '').trim() || !uploadQueue.length}
          >
            {uploadQueue.length > 1 ? `Save ${uploadQueue.length} audios` : 'Save audio'}
          </Button>,
        ]}
        destroyOnClose
      >
        {uploading && uploadProgress > 0 ? (
          <Progress percent={uploadProgress} style={{ marginBottom: 16 }} />
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Select
            style={{ width: '100%' }}
            placeholder="Select genre"
            value={uploadGenre}
            onChange={setUploadGenre}
            options={genreOptions}
            disabled={uploading}
            showSearch
            optionFilterProp="label"
            allowClear
          />
          {!catalogGenres.length ? (
            <span style={{ opacity: 0.7 }}>Use Add genre first, then pick it here.</span>
          ) : null}
          <div style={{ opacity: 0.75, fontSize: 13 }}>
            Pick many files for one genre. Each row has its own display name (saved as file name in the table). Multi-select in the file dialog or add more with the button again.
          </div>
          <Upload
            key={uploadFileListKey}
            multiple
            accept="audio/*"
            disabled={uploading}
            fileList={audioUploadFileList}
            beforeUpload={() => false}
            itemRender={(originNode, file) => {
              const f = file.originFileObj;
              const idx = f ? uploadQueue.findIndex((row) => row.file === f) : -1;
              if (idx < 0) return originNode;
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                    width: '100%',
                    marginBottom: 4,
                  }}
                >
                  <div style={{ flex: '0 1 auto', minWidth: 0 }}>{originNode}</div>
                  <Input
                    size="small"
                    placeholder="Display name"
                    style={{ flex: '1 1 220px', maxWidth: 420 }}
                    value={uploadQueue[idx].displayName}
                    onChange={(e) => setQueueItemDisplayName(idx, e.target.value)}
                    disabled={uploading}
                  />
                </div>
              );
            }}
            onChange={({ fileList }) => {
              const nextFiles = fileList.map((x) => x.originFileObj).filter((x) => x instanceof File);
              setUploadQueue((prev) => {
                const prevBySig = new Map(prev.map((row) => [fileSignature(row.file), row.displayName]));
                return nextFiles.map((f) => ({
                  file: f,
                  displayName: prevBySig.has(fileSignature(f)) ? prevBySig.get(fileSignature(f)) : f.name,
                }));
              });
            }}
          >
            <Button icon={<UploadOutlined />} disabled={uploading}>
              Select audio files
            </Button>
          </Upload>
        </div>
      </Modal>

      <Modal
        open={editOpen}
        onCancel={closeEdit}
        footer={[
          <Button key="cancel" onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={onSaveEdit}
            loading={saving}
            disabled={saving || !String(editGenre || '').trim()}
          >
            Save
          </Button>,
        ]}
        destroyOnClose
        title="Edit Audio"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Select
            style={{ width: '100%' }}
            placeholder="Select genre"
            value={editGenre}
            onChange={setEditGenre}
            options={editGenreOptions}
            disabled={saving}
            showSearch
            optionFilterProp="label"
            allowClear
          />
          <Input
            value={editFileName}
            onChange={(e) => setEditFileName(e.target.value)}
            placeholder="File name"
            disabled={saving}
          />
          <div>
            <input
              key={editFileInputKey}
              ref={editFileInputRef}
              type="file"
              accept="audio/*"
              onChange={(e) => {
                const next = e.target.files?.[0] || null;
                setEditFile(next);
                if (next?.name) setEditFileName(next.name);
              }}
              disabled={saving}
            />
          </div>
          {editFile?.name ? <div style={{ opacity: 0.7 }}>Selected: {editFile.name}</div> : null}
          {editUrl ? <audio controls src={editUrl} style={{ width: '100%' }} /> : null}
        </div>
      </Modal>
    </div>
  );
}

